/**
 * SmartLinkBanner — Astor smart-link banner (Documents Library)
 *
 * Mint-tinted banner shown above the drag-drop zone when one or more
 * documents in the library are unlinked from a loan. Astor offers to
 * link them automatically.
 *
 * Spec: /tmp/design-pass/borrower/specs/sign-and-documents.md (Astor surface 1)
 * - Conditional: documents.filter(d => !d.linkedLoan).length > 0
 * - Pattern mirrors AstorWalkBlock; will be folded into AstorBriefingCard
 *   once that design-system component lands.
 *
 * Rules:
 * - Glass components only (rule 11b)
 * - sx + theme tokens, alpha() for mint tint (rule 11c)
 * - All copy via t() (rule 13)
 * - 44px touch target on the CTA (portals.md)
 */

import { alpha, Box, baseColors, GlassButton, Stack, Typography, useI18n } from '@ops-matrix/design-system-ui';
import { AstorAvatar } from '../loans/AstorAvatar';

export interface SmartLinkBannerProps {
  /** Number of unlinked documents — drives the title count. */
  unlinkedCount: number;
  /** Click handler — parent opens the review-&-link flow. */
  onReviewAndLink: () => void;
}

export function SmartLinkBanner({ unlinkedCount, onReviewAndLink }: SmartLinkBannerProps) {
  const { t } = useI18n();
  if (unlinkedCount <= 0) return null;

  return (
    <Box
      role="region"
      aria-label={t('consumer.documents.astorSmartLinkTitle', { count: unlinkedCount })}
      sx={{
        p: 2,
        borderRadius: 1,
        border: 1,
        borderColor: alpha(baseColors.teal[400], 0.4),
        bgcolor: alpha(baseColors.teal[400], 0.08),
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: { md: 'center' },
        gap: 2,
      }}
    >
      <AstorAvatar size={24} pulse />
      <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body1" component="p" sx={{ fontWeight: 600 }}>
          {t('consumer.documents.astorSmartLinkTitle', { count: unlinkedCount })}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {t('consumer.documents.astorSmartLinkDetail')}
        </Typography>
      </Stack>
      <GlassButton
        size="small"
        variant="gradient"
        color="success"
        onClick={onReviewAndLink}
        sx={{ minHeight: 44, flexShrink: 0 }}
      >
        {t('consumer.documents.astorSmartLinkCta')}
      </GlassButton>
    </Box>
  );
}

export default SmartLinkBanner;
